import { Download, Loader2, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
  fristKurz,
  MAX_OPTION_LABEL,
  MAX_OPTIONS,
  MAX_POLL_BODY,
  MAX_POLL_TITLE,
  MIN_OPTIONS,
  type ResultsVisibility,
} from '../../../shared/polls';
import { adminApi, type AdminPoll } from '../../lib/api';
import { Meldung, type MeldungsInhalt } from '../../lib/Meldung';

interface FormState {
  title: string;
  body: string;
  options: string[];
  datum: string;
  zeit: string;
  multiple: boolean;
  anonymous: boolean;
  visibility: ResultsVisibility;
}

const LEER: FormState = {
  title: '',
  body: '',
  options: ['', ''],
  datum: '',
  zeit: '18:00',
  multiple: false,
  anonymous: false,
  visibility: 'creator',
};

const SICHTBARKEIT: { wert: ResultsVisibility; text: string }[] = [
  { wert: 'creator', text: 'Nur ich (z. B. Menü, Merch-Größen)' },
  { wert: 'all_after_close', text: 'Alle sehen die Zahlen nach Ende' },
  { wert: 'all_live', text: 'Alle sehen die Zahlen sofort' },
];

/**
 * Abfragen anlegen und auswerten (Design-System §6 „Abfrage“). Namen gibt es
 * nur im Export, nur fuer die anlegende Person und nie bei anonymen Abfragen –
 * das prueft der Server, hier wird der Knopf nur ausgeblendet.
 */
export function PollsPanel() {
  const [items, setItems] = useState<AdminPoll[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(LEER);
  const [meldung, setMeldung] = useState<MeldungsInhalt | null>(null);
  const meldungSchliessen = useCallback(() => setMeldung(null), []);
  const fehler = (err: unknown) =>
    setMeldung({ art: 'error', text: err instanceof Error ? err.message : String(err) });

  async function reload() {
    setLoading(true);
    try {
      setItems((await adminApi.listPolls()).polls);
    } catch (err) {
      fehler(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  function option(i: number, wert: string) {
    setForm({ ...form, options: form.options.map((o, j) => (j === i ? wert : o)) });
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMeldung(null);
    try {
      await adminApi.createPoll({
        title: form.title.trim(),
        body_md: form.body,
        options: form.options,
        // Lokale Zeit des Browsers (Berlin) → ISO; der Server prueft die Frist.
        closes_at: new Date(`${form.datum}T${form.zeit || '18:00'}`).toISOString(),
        multiple: form.multiple,
        anonymous: form.anonymous,
        results_visibility: form.visibility,
      });
      setForm(LEER);
      await reload();
      setMeldung({ art: 'success', text: 'Abfrage angelegt – sie steht jetzt im Schwarzen Brett.' });
    } catch (err) {
      fehler(err);
    } finally {
      setSaving(false);
    }
  }

  async function exportieren(poll: AdminPoll) {
    setPending(poll.id);
    setMeldung(null);
    try {
      const blob = await adminApi.exportPoll(poll.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `abfrage-${poll.title.replace(/[^\p{L}\p{N}]+/gu, '-').slice(0, 40)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setMeldung({ art: 'success', text: 'Liste heruntergeladen.' });
    } catch (err) {
      fehler(err);
    } finally {
      setPending(null);
    }
  }

  async function loeschen(poll: AdminPoll) {
    if (!window.confirm(`„${poll.title}" samt allen Antworten löschen?`)) return;
    setPending(poll.id);
    setMeldung(null);
    try {
      await adminApi.deletePoll(poll.id);
      await reload();
      setMeldung({ art: 'success', text: `„${poll.title}" wurde gelöscht.` });
    } catch (err) {
      fehler(err);
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="stack">
      <section className="card">
        <h2>Neue Abfrage</h2>
        <p className="card__meta">
          Erscheint im Schwarzen Brett. Zwei Tage vor Ende bekommen alle, die noch nicht
          geantwortet haben, eine Erinnerungsmail. 90 Tage nach Ende wird alles gelöscht.
        </p>
        <form onSubmit={submit}>
          <label className="field">
            <span className="field__label">Frage</span>
            <input
              className="input"
              type="text"
              required
              maxLength={MAX_POLL_TITLE}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field__label">Erklärung (optional, Markdown)</span>
            <textarea
              className="textarea"
              maxLength={MAX_POLL_BODY}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </label>

          <div className="field">
            <span className="field__label">Antwortoptionen</span>
            <div className="optlist">
              {form.options.map((o, i) => (
                <div className="optlist__row" key={i}>
                  <input
                    className="input"
                    type="text"
                    maxLength={MAX_OPTION_LABEL}
                    aria-label={`Option ${i + 1}`}
                    placeholder={`Option ${i + 1}`}
                    value={o}
                    onChange={(e) => option(i, e.target.value)}
                  />
                  {form.options.length > MIN_OPTIONS && (
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`Option ${i + 1} entfernen`}
                      onClick={() =>
                        setForm({ ...form, options: form.options.filter((_, j) => j !== i) })
                      }
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {form.options.length < MAX_OPTIONS && (
              <button
                type="button"
                className="px-btn px-btn--ghost px-btn--sm"
                onClick={() => setForm({ ...form, options: [...form.options, ''] })}
              >
                <Plus size={14} aria-hidden="true" /> Option
              </button>
            )}
            <p className="card__meta">
              Keine Freitextfelder: Fragen nach Allergien oder Unverträglichkeiten bitte direkt
              klären, nicht hier.
            </p>
          </div>

          <div className="form__row form__row--2">
            <label className="field">
              <span className="field__label">Frist (Datum)</span>
              <input
                className="input"
                type="date"
                required
                value={form.datum}
                onChange={(e) => setForm({ ...form, datum: e.target.value })}
              />
            </label>
            <label className="field">
              <span className="field__label">Uhrzeit</span>
              <input
                className="input"
                type="time"
                required
                value={form.zeit}
                onChange={(e) => setForm({ ...form, zeit: e.target.value })}
              />
            </label>
          </div>

          <label className="field">
            <span className="field__label">Wer sieht das Ergebnis?</span>
            <select
              className="select"
              value={form.visibility}
              onChange={(e) => setForm({ ...form, visibility: e.target.value as ResultsVisibility })}
            >
              {SICHTBARKEIT.map((s) => (
                <option key={s.wert} value={s.wert}>
                  {s.text}
                </option>
              ))}
            </select>
            <span className="card__meta">Andere sehen immer nur Zahlen, nie Namen.</span>
          </label>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.multiple}
              onChange={(e) => setForm({ ...form, multiple: e.target.checked })}
            />
            Mehrere Antworten erlaubt
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.anonymous}
              onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
            />
            Anonym – auch du siehst keine Namen, Antworten lassen sich nicht ändern
          </label>

          <div className="form__actions">
            <button type="submit" className="px-btn px-btn--sm" disabled={saving}>
              {saving ? (
                <Loader2 size={14} className="spin" aria-hidden="true" />
              ) : (
                <Plus size={14} aria-hidden="true" />
              )}{' '}
              Abfrage anschlagen
            </button>
          </div>
        </form>
      </section>

      <Meldung inhalt={meldung} onClose={meldungSchliessen} />

      <section className="card">
        <div className="section__head">
          <h2>Abfragen</h2>
          <span className="card__meta">{items.length}</span>
        </div>

        {loading ? (
          <div className="empty">Wird geladen …</div>
        ) : items.length === 0 ? (
          <div className="empty">Noch keine Abfrage angelegt.</div>
        ) : (
          items.map((poll) => (
            <article className="note" key={poll.id}>
              <div className="note__head">
                <h3 className="note__title">{poll.title}</h3>
                <span className="note__pills">
                  {poll.anonymous && <span className="badge-pill badge-pill--muted">anonym</span>}
                  <span className={`badge-pill${poll.open ? '' : ' badge-pill--muted'}`}>
                    {poll.open ? 'läuft' : 'beendet'}
                  </span>
                </span>
              </div>
              <p className="note__meta">
                {poll.open ? 'läuft bis' : 'beendet am'} {fristKurz(poll.closes_at)} ·{' '}
                {poll.participants} antworten{poll.mine ? ' · von dir' : ''}
              </p>

              {poll.results ? (
                <div className="erg" aria-label="Ergebnis">
                  {poll.options.map((o) => {
                    const n = poll.results?.find((r) => r.option_id === o.id)?.count ?? 0;
                    const basis = Math.max(1, poll.multiple ? poll.participants : poll.results!.reduce((s, r) => s + r.count, 0));
                    return (
                      <div className="erg__row" key={o.id}>
                        <span>{o.label}</span>
                        <span className="erg__n">{n}</span>
                        <div className="erg__bar" aria-hidden="true">
                          <div className="erg__fill" style={{ width: `${Math.round((n / basis) * 100)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="poll__hint">&gt; ergebnis sieht nur die anlegende person</p>
              )}

              <div className="form__actions">
                {poll.mine && !poll.anonymous && (
                  <button
                    type="button"
                    className="px-btn px-btn--ghost px-btn--sm"
                    disabled={pending === poll.id}
                    onClick={() => void exportieren(poll)}
                  >
                    <Download size={14} aria-hidden="true" /> Liste mit Namen
                  </button>
                )}
                <button
                  type="button"
                  className="px-btn px-btn--danger px-btn--sm"
                  disabled={pending === poll.id}
                  onClick={() => void loeschen(poll)}
                >
                  <Trash2 size={14} aria-hidden="true" /> Löschen
                </button>
              </div>
            </article>
          ))
        )}
      </section>
    </div>
  );
}
